import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Loader2, Package, Rocket, XCircle } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError } from "@hostdock/sdk";
import { AppLogo, Button, Checkbox, cn, EngineLogo, TechLogo, ErrorState, Field, FormError, Input, KeyValue, Notice, runtimeSupportNote, Skeleton } from "@hostdock/ui";
import { api } from "../api";
import { Banner, DetailCard, PageHero } from "../components/page-kit";
import { useLiveJob } from "../lib/live-job";
import { useSession } from "../session";
import { AddDomainForm, parentsOf } from "./domains";

type AppType = "WORDPRESS" | "WOOCOMMERCE" | "PHP" | "NODE" | "PYTHON" | "STATIC";
const STEPS = ["Application", "Domain", "Runtime", "Database", "Review", "Deploy"] as const;
const STEP_LABEL = (i: number, wp: boolean) => (i === 3 && wp ? "WordPress" : STEPS[i]);
const APPS: { type: AppType; name: string; description: string; runtime: "php" | "node" | "python" | "static" }[] = [
  { type: "WORDPRESS", name: "WordPress", description: "Installed and configured for you: database, SSL, cron and optional Redis cache.", runtime: "php" },
  { type: "WOOCOMMERCE", name: "WooCommerce", description: "WordPress with the WooCommerce store plugin ready to set up.", runtime: "php" },
  { type: "PHP", name: "PHP", description: "Any PHP site or framework: Laravel, custom code, static sites.", runtime: "php" },
  { type: "NODE", name: "Node.js", description: "Express, Next.js, NestJS and other Node applications.", runtime: "node" },
  { type: "PYTHON", name: "Python", description: "Django, Flask, FastAPI or your own app.", runtime: "python" },
  { type: "STATIC", name: "Start From Scratch", description: "A blank static website: upload your own HTML, CSS and JavaScript. No application, database or framework.", runtime: "static" },
];
const RUNTIME_NAME: Record<"php" | "node" | "python" | "static", string> = { php: "PHP", node: "Node.js", python: "Python", static: "Static" };
const isWp = (t: AppType) => t === "WORDPRESS" || t === "WOOCOMMERCE";
const isStaticApp = (t: AppType) => t === "STATIC";
const WP_USER = /^[a-z0-9._-]{3,60}$/;

export function Choice({ selected, onClick, title, description, disabled, logo }: { selected: boolean; onClick(): void; title: string; description?: string; disabled?: boolean; logo?: ReactNode }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={cn("relative flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-[border-color,background-color,box-shadow] disabled:opacity-50", selected ? "border-[#048d9f] bg-[#effafb] ring-1 ring-[#048d9f] dark:bg-accent-soft" : "border-line bg-surface hover:border-line-strong hover:shadow-sm")}
    >
      {logo}
      <span className={cn("flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border", logo ? "absolute top-3 right-3" : "mt-0.5", selected ? "border-[#048d9f] bg-[#048d9f] text-white" : "border-line-strong")}>{selected && <Check size={11} strokeWidth={3} />}</span>
      <span className={logo ? "min-w-0 pr-6" : undefined}>
        <span className="block text-[14px] font-medium">{title}</span>
        {description && <span className="block text-[12.5px] text-muted">{description}</span>}
      </span>
    </button>
  );
}

export function CreateWebsitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const opts = useQuery({ queryKey: ["hosting-options"], queryFn: api.hosting.options });
  // Websites are created on a domain from the customer's list (added on the Domains page, or right here).
  const qc = useQueryClient();
  const doms = useQuery({ queryKey: ["domains"], queryFn: () => api.domains.list({ pageSize: 100 }) });
  const listed = (doms.data?.items ?? []).filter((d) => !d.websiteId);
  const [addingDomain, setAddingDomain] = useState(false);
  const subs = opts.data?.subscriptions ?? [];
  const [step, setStep] = useState(0);
  const { session } = useSession();
  const [f, setF] = useState({ subscriptionId: "", appType: "WORDPRESS" as AppType, domain: params.get("domain") ?? "", wwwAlias: true, runtimeVersion: "", database: "" as "" | "MARIADB" | "POSTGRESQL" | "MONGODB", redis: false });
  // A domain preselected from the Domains page: no www. alias for a subdomain.
  const [preset, setPreset] = useState(Boolean(params.get("domain")));
  useEffect(() => {
    if (!preset || !doms.data) return;
    setPreset(false);
    setF((v) => ({ ...v, wwwAlias: !parentsOf(v.domain, doms.data.items).length }));
  }, [preset, doms.data]);
  const [wpf, setWpf] = useState({ title: "", adminUsername: "", adminEmail: session.user.email });
  const [jobId, setJobId] = useState<string | null>(null);
  const [websiteId, setWebsiteId] = useState<string | null>(null);
  const { job, logs } = useLiveJob(jobId);
  const sub = subs.find((s) => s.id === f.subscriptionId) ?? (subs.length === 1 ? subs[0]! : undefined);
  const create = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.websites.create({
      subscriptionId: sub!.id, appType: f.appType, domain: f.domain, wwwAlias: f.wwwAlias, runtimeVersion: f.runtimeVersion,
      database: isWp(f.appType) ? { engine: "MARIADB" } : f.database ? { engine: f.database } : null, redis: f.redis,
      ...(isWp(f.appType) ? { wordpress: { title: wpf.title || f.domain, adminUsername: wpf.adminUsername, adminEmail: wpf.adminEmail } } : {}),
    }),
    // The chosen domain is now on the website: the Domains list must not keep showing it as unused.
    onSuccess: (r) => { setJobId(r.job.id); setWebsiteId(r.website.id); setStep(5); void qc.invalidateQueries({ queryKey: ["domains"] }); void qc.invalidateQueries({ queryKey: ["websites"] }); },
  });

  const hero = <PageHero icon={Rocket} title="Create website" description="HostDock sets up hosting, the domain and the database for you." />;
  if (opts.error) return <div className="flex flex-col lg:px-[13px]">{hero}<div className="hd-card mt-[15px] p-6"><ErrorState error={opts.error} onRetry={() => void opts.refetch()} /></div></div>;
  if (!opts.data) return <div className="flex flex-col lg:px-[13px]">{hero}<Skeleton className="mt-[15px] h-80 w-full rounded-xl" /></div>;
  if (!subs.length) {
    return (
      <div className="flex flex-col lg:px-[13px]">
        {hero}
        <Banner icon={Package} title="No active hosting plan">Your account doesn't have an active hosting package yet. Contact support to get started.</Banner>
      </div>
    );
  }
  const app = APPS.find((a) => a.type === f.appType)!;
  const versions = app.runtime === "static" ? [] : (sub?.runtimes[app.runtime] ?? []);
  const version = f.runtimeVersion || versions[versions.length - 1] || "";
  const full = sub ? sub.usage.websites >= sub.limits.websites : false;
  const domainOk = listed.some((d) => d.name === f.domain);
  // A subdomain (blog.example.com) does not get a www. alias by default.
  const pickDomain = (name: string) => setF({ ...f, domain: name, wwwAlias: !parentsOf(name, doms.data?.items ?? []).length });
  // A static site has no application runtime, so it needs no runtime catalogue entry or app-type limit — only a plan.
  const appAllowed = (t: AppType) => isStaticApp(t) ? Boolean(sub)
    : (sub?.runtimes[APPS.find((a) => a.type === t)!.runtime as "php" | "node" | "python"].length ?? 0) > 0
      && (t === "NODE" ? (sub?.limits.nodeApps ?? 0) > 0 : t === "PYTHON" ? (sub?.limits.pythonApps ?? 0) > 0 : true)
      && (!isWp(t) || (sub?.limits.databases ?? 0) > 0);
  const wpOk = !isWp(f.appType) || (WP_USER.test(wpf.adminUsername) && wpf.adminUsername !== "admin" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(wpf.adminEmail));
  const staticApp = isStaticApp(f.appType);
  // Static sites skip the runtime and database steps (nothing to configure), so those steps always pass.
  const canNext = [Boolean(sub && !full) && appAllowed(f.appType), domainOk, staticApp || Boolean(version), staticApp || wpOk, true][step];
  const fe = create.error instanceof ApiError ? create.error.fieldErrors : {};

  const labels = STEPS.map((_s, i) => STEP_LABEL(i, isWp(f.appType)));
  const TITLES = [
    "What are you building?",
    "Which domain will it use?",
    staticApp ? "No runtime needed" : `Choose a ${RUNTIME_NAME[app.runtime]} version`,
    staticApp ? "No database needed" : isWp(f.appType) ? "Set up WordPress" : "Add a database?",
    "Review and deploy",
    job?.status === "SUCCESS" ? "Deployed" : job?.status === "FAILED" ? "Deployment stopped" : "Deploying",
  ];

  return (
    <div className="flex flex-col lg:px-[13px]">
      {hero}
      <ol className="hd-card mt-[10px] flex gap-x-3 overflow-x-auto px-[18px] py-[14px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Steps">
        {labels.map((s, i) => (
          <li key={s} className="flex min-w-0 shrink-0 items-center gap-[10px] lg:flex-1" aria-current={i === step ? "step" : undefined}>
            <span className={cn("flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[12.5px] font-semibold tabular-nums",
              i < step ? "bg-[#048d9f] text-white" : i === step ? "bg-[#d6f5f7] text-[#0a8ea0] ring-2 ring-[#048d9f] dark:bg-accent-soft dark:text-accent" : "bg-surface-3 text-muted")}>
              {i < step ? <Check size={15} strokeWidth={2.5} /> : i + 1}
            </span>
            <span className="leading-tight">
              <span className="block text-[10.5px] tracking-wide text-subtle uppercase max-sm:hidden">Step {i + 1}</span>
              <span className={cn("block text-[13px] whitespace-nowrap", i === step ? "font-semibold text-fg" : i < step ? "font-medium text-[#0a8ea0] dark:text-accent" : "text-muted")}>{s}</span>
            </span>
            {i < labels.length - 1 && <span className={cn("ml-1 hidden h-[2px] min-w-4 flex-1 rounded-full lg:block", i < step ? "bg-[#048d9f]" : "bg-line")} aria-hidden />}
          </li>
        ))}
      </ol>
      <div className={cn("mt-[15px] grid items-start gap-[15px]", step < 4 && "xl:grid-cols-[minmax(0,1fr)_340px]")}>
      <DetailCard title={TITLES[step]}>
        {step === 0 && (
          <div className="flex flex-col gap-4">
            {subs.length > 1 && (
              <div className="flex flex-col gap-2">
                <p className="text-[13px] font-medium">Hosting package</p>
                {subs.map((s) => <Choice key={s.id} selected={f.subscriptionId === s.id} onClick={() => setF({ ...f, subscriptionId: s.id })} title={s.plan.name} description={`${s.usage.websites} of ${s.limits.websites} websites used`} />)}
              </div>
            )}
            {full && <Notice tone="warning" title="Website limit reached">Your {sub?.plan.name} plan allows {sub?.limits.websites} website(s). Delete one or upgrade your plan.</Notice>}
            <div className="grid gap-3 sm:grid-cols-2">
              {APPS.map((a) => <Choice key={a.type} logo={<AppLogo appType={a.type} size="lg" />} selected={f.appType === a.type} disabled={!appAllowed(a.type)} onClick={() => setF({ ...f, appType: a.type, runtimeVersion: "" })} title={a.name} description={appAllowed(a.type) ? a.description : "Not included in your plan"} />)}
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="flex flex-col gap-4">
            {doms.error && <ErrorState error={doms.error} onRetry={() => void doms.refetch()} />}
            {doms.isPending && <Skeleton className="h-24 w-full rounded-xl" />}
            {doms.data && listed.length > 0 && (
              <div className="flex flex-col gap-2" role="radiogroup" aria-label="Your domains">
                <p className="text-[12.5px] text-muted">Choose one of your domains. You can point its DNS later; the site works over HTTP until then, and HTTPS is enabled automatically once DNS points to HostDock.</p>
                {listed.map((d) => (
                  <Choice key={d.id} selected={f.domain === d.name} onClick={() => pickDomain(d.name)} title={d.name}
                    description={d.status === "VERIFIED" ? "Ownership verified" : "Waiting for its DNS record (you can still create the website now)"} />
                ))}
              </div>
            )}
            {fe.domain && <p className="text-[12.5px] text-danger">{fe.domain}</p>}
            {doms.data && (listed.length === 0 || addingDomain) ? (
              <div className="rounded-xl border border-line p-4">
                {listed.length === 0 && <p className="mb-3 text-[13px] text-muted">Add your domain first. It goes on your Domains list, where you can also check its DNS.</p>}
                <AddDomainForm autoFocus onAdded={(d) => { setAddingDomain(false); pickDomain(d.name); }} />
              </div>
            ) : doms.data && <button type="button" className="self-start text-[12.5px] font-medium text-accent hover:underline" onClick={() => setAddingDomain(true)}>Add another domain</button>}
            {!f.domain.startsWith("www.") && <Checkbox checked={f.wwwAlias} onChange={(v) => setF({ ...f, wwwAlias: v })} label={`Also use www.${f.domain || "example.com"}`} />}
          </div>
        )}
        {step === 2 && (staticApp ? (
          <Notice>A static site has no application runtime. Your files in <code>public/</code> are served directly, so there is no PHP, Node.js or Python version to choose.</Notice>
        ) : (
          <div className="flex flex-col gap-2">
            {[...versions].reverse().map((v, i) => <Choice key={v} selected={version === v} onClick={() => setF({ ...f, runtimeVersion: v })} logo={<TechLogo tech={app.runtime as "php" | "node" | "python"} size="sm" />} title={`${RUNTIME_NAME[app.runtime]} ${v}`} description={i === 0 ? "Latest (recommended)" : runtimeSupportNote(app.runtime as "php" | "node" | "python", v)?.long} />)}
          </div>
        ))}
        {step === 3 && staticApp && (
          <Notice>A static site has no database. Upload your HTML, CSS and JavaScript to <code>public/</code> after it deploys, over SFTP, the File Manager or Git.</Notice>
        )}
        {step === 3 && !staticApp && isWp(f.appType) && (
          <div className="flex flex-col gap-4">
            <Notice>WordPress gets its own MariaDB database, created automatically. You sign in to WordPress from this panel, so there is no password to remember.</Notice>
            <Field label="Site title" error={fe["wordpress.title"]}>{(p) => <Input {...p} value={wpf.title} onChange={(e) => setWpf({ ...wpf, title: e.target.value })} placeholder={f.domain || "My website"} />}</Field>
            <Field label="Administrator username" hint="Lowercase letters, digits, dot, dash or underscore. Avoid 'admin' — it is the first name attackers try." error={fe["wordpress.adminUsername"] ?? (wpf.adminUsername === "admin" ? "Choose a username other than 'admin'" : undefined)}>
              {(p) => <Input {...p} value={wpf.adminUsername} onChange={(e) => setWpf({ ...wpf, adminUsername: e.target.value.trim().toLowerCase() })} autoComplete="off" />}
            </Field>
            <Field label="Administrator email" error={fe["wordpress.adminEmail"]}>{(p) => <Input {...p} type="email" value={wpf.adminEmail} onChange={(e) => setWpf({ ...wpf, adminEmail: e.target.value.trim() })} />}</Field>
            {sub?.features.redis && <Checkbox checked={f.redis} onChange={(v) => setF({ ...f, redis: v })} label="Enable Redis object cache" description="Speeds up WordPress by caching database queries in a private Redis store." />}
          </div>
        )}
        {step === 3 && !staticApp && !isWp(f.appType) && (
          <div className="flex flex-col gap-2">
            <Choice selected={f.database === ""} onClick={() => setF({ ...f, database: "" })} title="No database" />
            <Choice logo={<EngineLogo engine="MARIADB" size="md" />} selected={f.database === "MARIADB"} onClick={() => setF({ ...f, database: "MARIADB" })} title="MariaDB" description="MySQL-compatible. Best for PHP applications." />
            <Choice logo={<EngineLogo engine="POSTGRESQL" size="md" />} selected={f.database === "POSTGRESQL"} onClick={() => setF({ ...f, database: "POSTGRESQL" })} title="PostgreSQL" description="Advanced open-source SQL database." />
            {sub?.features.mongodb && <Choice logo={<EngineLogo engine="MONGODB" size="md" />} selected={f.database === "MONGODB"} onClick={() => setF({ ...f, database: "MONGODB" })} title="MongoDB" description="Document database for JSON-shaped data." />}
            {sub?.features.redis && <div className="mt-2"><Checkbox checked={f.redis} onChange={(v) => setF({ ...f, redis: v })} label="Enable Redis cache" description="A private Redis user for this website." /></div>}
          </div>
        )}
        {step === 4 && (
          <div className="flex flex-col gap-4">
            <KeyValue items={[
              { label: "Package", value: sub?.plan.name },
              { label: "Application", value: app.name },
              { label: "Domain", value: `${f.domain}${f.wwwAlias && !f.domain.startsWith("www.") ? ` (+ www.${f.domain})` : ""}` },
              { label: "Runtime", value: staticApp ? "None (static site, files served directly)" : `${RUNTIME_NAME[app.runtime]} ${version}` },
              { label: "Database", value: staticApp ? "None" : isWp(f.appType) ? "MariaDB (created for WordPress)" : f.database ? { MARIADB: "MariaDB", POSTGRESQL: "PostgreSQL", MONGODB: "MongoDB" }[f.database] : "None" },
              ...(isWp(f.appType) ? [{ label: "WordPress", value: `${wpf.title || f.domain} · administrator ${wpf.adminUsername} (${wpf.adminEmail})` }] : []),
              { label: "Redis", value: f.redis ? "Enabled" : "Off" },
              { label: "Resources", value: sub ? `Shares the package's ${sub.limits.cpuCores} CPU · ${sub.limits.memoryMb} MB RAM with its other websites` : "" },
            ]} />
            {create.error && <FormError error={create.error} />}
          </div>
        )}
        {step === 5 && <DeployProgress job={job} logs={logs.map((l) => l.message)} websiteId={websiteId} wordpress={isWp(f.appType)} />}
        {step < 5 && (
          <div className="mt-6 flex justify-between gap-2 border-t border-line pt-4">
            <Button variant="ghost" onClick={() => (step === 0 ? navigate("/websites") : setStep(step - 1))}>{step === 0 ? "Cancel" : "Back"}</Button>
            {step < 4 ? (
              <Button variant="primary" disabled={!canNext} onClick={() => { if (step === 2 && !f.runtimeVersion) setF({ ...f, runtimeVersion: version }); setStep(step + 1); }}>Continue</Button>
            ) : (
              <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>Deploy website</Button>
            )}
          </div>
        )}
      </DetailCard>
      {step < 4 && (
        <aside className="hd-card px-[18px] py-[16px]" aria-label="Your new website">
          <h2 className="text-[14.5px] font-bold text-fg">Your new website</h2>
          <div className="mt-[12px] flex items-center gap-[12px] rounded-xl bg-surface-2/70 px-[12px] py-[10px]">
            <AppLogo appType={app.type} size="md" />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-[13.5px] font-semibold">{f.domain || "Domain not chosen yet"}</p>
              <p className="mt-[3px] text-[12px] text-muted">{app.name}{step >= 2 && version ? ` · ${RUNTIME_NAME[app.runtime]} ${version}` : ""}</p>
            </div>
          </div>
          <dl className="mt-[12px] flex flex-col divide-y divide-line text-[12.5px]">
            <SummaryRow label="Package">{sub ? `${sub.plan.name} · ${sub.usage.websites} of ${sub.limits.websites} websites used` : "Choose a package"}</SummaryRow>
            <SummaryRow label="Database">{isWp(f.appType) ? "MariaDB, set up automatically" : f.database ? { MARIADB: "MariaDB", POSTGRESQL: "PostgreSQL", MONGODB: "MongoDB" }[f.database] : step >= 3 ? "None" : "Chosen in step 4"}</SummaryRow>
            {sub && <SummaryRow label="Resources">Shares {sub.limits.cpuCores} CPU · {sub.limits.memoryMb} MB RAM with the package</SummaryRow>}
            <SummaryRow label="HTTPS">Issued automatically once DNS points to HostDock</SummaryRow>
          </dl>
        </aside>
      )}
      </div>
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-[9px]">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

const PHASES: { match: RegExp; label: string; wp?: boolean }[] = [
  { match: /Selecting|Placed/, label: "Select server" },
  { match: /account|directories/, label: "Prepare hosting account" },
  { match: /database|Database/, label: "Create database" },
  { match: /workload|Application running|Starting/, label: "Start application" },
  { match: /WordPress/, label: "Install WordPress", wp: true },
  { match: /domain|Proxy|Configuring/, label: "Configure domain" },
  { match: /Health/, label: "Health check" },
];

function DeployProgress({ job, logs, websiteId, wordpress }: { job: { status: string; progress: number; progressMessage: string | null; error: string | null } | null; logs: string[]; websiteId: string | null; wordpress: boolean }) {
  const phases = PHASES.filter((p) => !p.wp || wordpress);
  const done = job?.status === "SUCCESS";
  const failed = job?.status === "FAILED";
  const text = [...logs, job?.progressMessage ?? ""].join("\n");
  const reached = phases.map((p) => p.match.test(text));
  const last = reached.lastIndexOf(true);
  return (
    <div className="flex flex-col gap-4" aria-live="polite">
      <div className="flex items-center gap-3">
        {done ? <CheckCircle2 className="text-success" /> : failed ? <XCircle className="text-danger" /> : <Loader2 className="animate-spin text-accent" />}
        <div>
          <p className="text-[15px] font-semibold">{done ? "Your website is live" : failed ? "Deployment failed" : "Deploying your website…"}</p>
          <p className="text-[12.5px] text-muted">{done ? "Everything is set up." : failed ? job?.error : job?.progressMessage ?? "Queued"}</p>
        </div>
      </div>
      <div className="h-[7px] overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={job?.progress ?? 0} aria-valuemin={0} aria-valuemax={100}>
        <div className={cn("h-full rounded-full transition-all", failed ? "bg-danger" : done ? "bg-[#14a04a]" : "bg-[#048d9f]")} style={{ width: `${done ? 100 : job?.progress ?? 2}%` }} />
      </div>
      <ul className="flex flex-col gap-1.5">
        {phases.map((p, i) => {
          const state = done || i < last ? "done" : i === last && !failed ? "active" : failed && i === last ? "failed" : "pending";
          return (
            <li key={p.label} className="flex items-center gap-2 text-[13.5px]">
              {state === "done" ? <Check size={15} className="text-success" /> : state === "active" ? <Loader2 size={15} className="animate-spin text-accent" /> : state === "failed" ? <XCircle size={15} className="text-danger" /> : <span className="h-[15px] w-[15px] rounded-full border border-line-strong" />}
              <span className={state === "pending" ? "text-subtle" : undefined}>{p.label}</span>
            </li>
          );
        })}
      </ul>
      {(done || failed) && websiteId && (
        <div className="flex gap-2">
          <Link to={`/websites/${websiteId}`}><Button variant="primary">Open website dashboard</Button></Link>
        </div>
      )}
    </div>
  );
}
